// src/pages/task/TaskDependencyGraphPage.tsx
// Dependency graph for tasks: nodes (tasks with status) and edges
// (dependencies). Highlights the critical path and any cycle the layout
// finds. Renders an inline SVG — no charting dependency. Wraps
// GetDependencyGraph.
//
// The server supports scoping this down via `scope` (product), `parent`
// (project/epic entity) and `max_depth` — see
// services/task/internal/services/v1/get_dependency_graph.go. The generated
// client cannot send them yet: `GetDependencyGraph(params?: {})` drops every
// message-typed request field (bd framework-tdz1, generator fix in
// progress). Until it lands, this page fetches the unscoped graph once and
// narrows it client-side: a focus task's neighbourhood via BFS over the
// returned edges, a picked parent by filtering the returned nodes, and
// "only my tasks" by filtering on the node's assignee. None of that reduces
// what is fetched — only what is drawn. See loadGraph() below for the
// one-line change that switches parent/max_depth to server-side filtering
// once the generator fix ships.

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useI18n, useAuth } from '@react/hooks';
import { Alert, Badge, Button, ErrorAlert, Card, CardContent } from '@react/components';
import { Select } from '@/components/Select';
import { formatEnumLabel, getEntityId } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';
import { useTaskSearch } from '@/features/task/useTaskSearch';
import { idOf, walkNeighborhood, getNodeParentId, getNodeAssigneeId } from '@/features/task/graphLogic';
import type { Task } from '@core/types/task';

const COL_W = 220;
const ROW_H = 92;
const NODE_W = 176;
const NODE_H = 64;
const PAD = 24;

const DEPTH_OPTIONS = ['1', '2', '3', '4', '5', 'all'];
const DEFAULT_DEPTH = '2';

interface Laid {
  id: string;
  node: any;
  x: number;
  y: number;
  inCycle: boolean;
}

interface LaidEdge {
  from: string;
  to: string;
  resolved: boolean;
  back: boolean;
}

// Longest-path layering with DFS cycle detection.
function layout(nodes: any[], edges: any[]) {
  const outAdj = new Map<string, { to: string; edge: any }[]>();
  const indeg = new Map<string, number>();
  const ids: string[] = [];
  for (const n of nodes) {
    const id = idOf(n);
    if (!id) continue;
    ids.push(id);
    outAdj.set(id, []);
    indeg.set(id, 0);
  }
  const laidEdges: LaidEdge[] = [];
  for (const e of edges) {
    const from = idOf(e.from_task);
    const to = idOf(e.to_task);
    if (!outAdj.has(from) || !outAdj.has(to)) continue;
    outAdj.get(from)!.push({ to, edge: e });
    indeg.set(to, (indeg.get(to) || 0) + 1);
  }

  // DFS colouring: 0=white,1=grey,2=black. Grey→grey is a back edge (cycle).
  const color = new Map<string, number>();
  const cycleNodes = new Set<string>();
  const backEdges = new Set<string>();
  const dfs = (u: string, stack: Set<string>) => {
    color.set(u, 1);
    stack.add(u);
    for (const { to } of outAdj.get(u) || []) {
      if (stack.has(to)) {
        backEdges.add(`${u}->${to}`);
        cycleNodes.add(u);
        cycleNodes.add(to);
      } else if ((color.get(to) || 0) === 0) {
        dfs(to, stack);
      }
    }
    stack.delete(u);
    color.set(u, 2);
  };
  for (const id of ids) if ((color.get(id) || 0) === 0) dfs(id, new Set());

  // Level = longest path from a source, memoised (safe because back edges skipped).
  const level = new Map<string, number>();
  const computeLevel = (u: string, seen: Set<string>): number => {
    if (level.has(u)) return level.get(u)!;
    if (seen.has(u)) return 0;
    seen.add(u);
    let best = 0;
    for (const { to } of outAdj.get(u) || []) {
      if (backEdges.has(`${u}->${to}`)) continue;
      best = Math.max(best, computeLevel(to, seen) + 1);
    }
    seen.delete(u);
    level.set(u, best);
    return best;
  };
  for (const id of ids) computeLevel(id, new Set());

  for (const e of edges) {
    const from = idOf(e.from_task);
    const to = idOf(e.to_task);
    if (!outAdj.has(from) || !outAdj.has(to)) continue;
    laidEdges.push({ from, to, resolved: !!e.is_resolved, back: backEdges.has(`${from}->${to}`) });
  }

  // Bucket by level, assign rows.
  const buckets = new Map<number, string[]>();
  for (const id of ids) {
    const lv = level.get(id) || 0;
    if (!buckets.has(lv)) buckets.set(lv, []);
    buckets.get(lv)!.push(id);
  }
  const pos = new Map<string, { x: number; y: number }>();
  const maxLevel = Math.max(0, ...ids.map((id) => level.get(id) || 0));
  let maxRows = 0;
  for (const [lv, bucket] of buckets) {
    maxRows = Math.max(maxRows, bucket.length);
    bucket.forEach((id, row) => {
      // Reverse level so sources (highest longest-path) sit left → right toward leaves.
      pos.set(id, { x: (maxLevel - lv) * COL_W + PAD, y: row * ROW_H + PAD });
    });
  }
  const nodeById = new Map(nodes.map((n) => [idOf(n), n]));
  const laid: Laid[] = ids.map((id) => ({
    id,
    node: nodeById.get(id),
    x: pos.get(id)!.x,
    y: pos.get(id)!.y,
    inCycle: cycleNodes.has(id),
  }));
  const width = (maxLevel + 1) * COL_W + PAD;
  const height = maxRows * ROW_H + PAD;
  return { laid, laidEdges, width, height, hasCycle: cycleNodes.size > 0, posOf: pos };
}

// ---------------------------------------------------------------------------
// TaskPickerField — search-as-you-type task picker, same interaction pattern
// as the dependency-target picker in TaskDependencies.tsx.
// ---------------------------------------------------------------------------

interface TaskPickerFieldProps {
  ariaLabel: string;
  placeholder: string;
  excludeId?: string;
  onPick: (task: Task) => void;
}

function TaskPickerField({ ariaLabel, placeholder, excludeId, onPick }: TaskPickerFieldProps) {
  const { t } = useI18n();
  const [query, setQuery] = useState('');
  const { results, searching } = useTaskSearch(query, excludeId);

  return (
    <div className="relative">
      <input
        type="text"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground"
      />
      {query.trim() && (
        <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-sm">
          {searching ? (
            <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('common.searching')}</p>
          ) : results.length === 0 ? (
            <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('common.noResults')}</p>
          ) : (
            results.map((tk) => {
              const tid = getEntityId(tk);
              return (
                <button
                  key={tid}
                  type="button"
                  onClick={() => { onPick(tk); setQuery(''); }}
                  className="block w-full truncate px-2.5 py-1.5 text-left text-sm text-foreground hover:bg-muted"
                >
                  {tk.title || tid.substring(0, 8)}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const TaskDependencyGraphPage = observer(() => {
  const store = useTask();
  const { t } = useI18n();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const focusTaskId = searchParams.get('task') || '';
  const showAll = searchParams.get('all') === '1';

  const [depth, setDepth] = useState(DEFAULT_DEPTH);
  const [parentId, setParentId] = useState('');
  const [parentLabel, setParentLabel] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  const [data, setData] = useState<{ nodes: any[]; edges: any[]; critical: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shouldShowGraph = !!focusTaskId || !!parentId || showAll;
  const me = auth.activeProfileId || '';

  const loadGraph = async () => {
    setLoading(true);
    setError(null);
    try {
      // NOTE(framework-tdz1): once the generated client accepts message-typed
      // request fields, this becomes:
      //   store.GetDependencyGraph({
      //     parent: parentId ? { id: parentId, ...restOfEntity } : undefined,
      //     max_depth: depth === 'all' ? undefined : Number(depth),
      //   })
      // and the client-side neighbourhood walk / parent filter below can be
      // deleted — the server would already have done the narrowing.
      const res: any = await store.GetDependencyGraph();
      const critical = (res?.critical_path || []).map((e: any) => e?.id || e?.entity?.id).filter(Boolean);
      setData({ nodes: res?.nodes || [], edges: res?.edges || [], critical });
    } catch (e) {
      setError(errMsg(e, t('task.graph.loadError')));
    } finally {
      setLoading(false);
    }
  };

  // Fetch once, the first time any scope (focus/parent/show-all) is active.
  // Changing focus/parent/depth/only-mine afterwards only recomputes the
  // client-side view below — the underlying fetch never changes shape today.
  useEffect(() => {
    if (shouldShowGraph && !data && !loading) {
      loadGraph();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowGraph]);

  const backToPicker = () => {
    setSearchParams({});
    setParentId('');
    setParentLabel('');
  };

  const clearFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('task');
    setSearchParams(next);
  };

  // -- Client-side scoping ---------------------------------------------------

  type ScopeNote = 'clientSideFiltered' | 'parentDataUnavailable' | null;

  const scoped = useMemo(() => {
    if (!data) return null;
    if (focusTaskId) {
      const depthN = depth === 'all' ? null : Number(depth);
      const ids = walkNeighborhood(data.edges, focusTaskId, depthN);
      if (!ids.has(focusTaskId)) {
        return { notFound: true as const, nodes: [], edges: [], note: null as ScopeNote };
      }
      const nodes = data.nodes.filter((n) => ids.has(idOf(n)));
      const edges = data.edges.filter((e) => ids.has(idOf(e.from_task)) && ids.has(idOf(e.to_task)));
      return { notFound: false as const, nodes, edges, note: null as ScopeNote };
    }

    if (parentId) {
      const anyParentData = data.nodes.some((n) => !!getNodeParentId(n));
      if (!anyParentData) {
        return { notFound: false as const, nodes: data.nodes, edges: data.edges, note: 'parentDataUnavailable' as ScopeNote };
      }
      const nodes = data.nodes.filter((n) => getNodeParentId(n) === parentId);
      const nodeIds = new Set(nodes.map(idOf));
      const edges = data.edges.filter((e) => nodeIds.has(idOf(e.from_task)) && nodeIds.has(idOf(e.to_task)));
      return { notFound: false as const, nodes, edges, note: 'clientSideFiltered' as ScopeNote };
    }

    // showAll
    return { notFound: false as const, nodes: data.nodes, edges: data.edges, note: null as ScopeNote };
  }, [data, focusTaskId, depth, parentId]);

  const mineScoped = useMemo(() => {
    if (!scoped || scoped.notFound) return scoped;
    if (!onlyMine || !me) return scoped;
    const nodes = scoped.nodes.filter((n) => getNodeAssigneeId(n) === me);
    const nodeIds = new Set(nodes.map(idOf));
    const edges = scoped.edges.filter((e) => nodeIds.has(idOf(e.from_task)) && nodeIds.has(idOf(e.to_task)));
    return { ...scoped, nodes, edges };
  }, [scoped, onlyMine, me]);

  const computed = useMemo(
    () => (mineScoped && !mineScoped.notFound ? layout(mineScoped.nodes, mineScoped.edges) : null),
    [mineScoped],
  );
  const criticalSet = useMemo(() => new Set(data?.critical || []), [data]);
  const focusNode = useMemo(
    () => (focusTaskId ? data?.nodes.find((n) => idOf(n) === focusTaskId) : undefined),
    [data, focusTaskId],
  );

  // -- Render -----------------------------------------------------------------

  const header = (
    <div>
      <nav className="text-sm mb-3">
        <Link to="/tasks" className="text-muted-foreground hover:text-foreground">{t('task.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('task.graph.title')}</span>
      </nav>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('task.graph.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('task.graph.description')}</p>
        </div>
        {shouldShowGraph && (
          <Button variant="secondary" size="sm" onClick={loadGraph} isLoading={loading}>{t('task.refresh')}</Button>
        )}
      </div>
    </div>
  );

  // No scope chosen yet: don't dump the whole product by default.
  if (!shouldShowGraph) {
    return (
      <div className="space-y-6">
        {header}
        <Card>
          <CardContent className="space-y-6 py-10">
            <div className="mx-auto max-w-md space-y-1.5 text-center">
              <h3 className="text-lg font-medium text-foreground">{t('task.graph.pickTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('task.graph.pickHint')}</p>
            </div>

            <div className="mx-auto max-w-sm space-y-4 text-left">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('task.graph.focusPickerLabel')}
                </label>
                <div className="mt-1">
                  <TaskPickerField
                    ariaLabel={t('task.graph.focusPickerLabel')}
                    placeholder={t('task.graph.focusSearchPlaceholder')}
                    onPick={(tk) => setSearchParams({ task: getEntityId(tk) })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground" aria-hidden="true">
                <span className="h-px flex-1 bg-border" />
                {t('task.graph.or')}
                <span className="h-px flex-1 bg-border" />
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('task.graph.parentPickerLabel')}
                </label>
                <div className="mt-1">
                  {parentId ? (
                    <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5">
                      <span className="truncate text-sm text-foreground">{parentLabel || parentId}</span>
                      <button
                        type="button"
                        onClick={() => { setParentId(''); setParentLabel(''); }}
                        className="shrink-0 text-xs text-muted-foreground hover:text-destructive"
                      >
                        {t('common.remove')}
                      </button>
                    </div>
                  ) : (
                    <TaskPickerField
                      ariaLabel={t('task.graph.parentPickerLabel')}
                      placeholder={t('task.graph.parentPickerPlaceholder')}
                      onPick={(tk) => { setParentId(getEntityId(tk)); setParentLabel(tk.title || ''); }}
                    />
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{t('task.graph.parentHint')}</p>
              </div>
            </div>

            <div className="mx-auto max-w-sm space-y-1 border-t border-border pt-5 text-center">
              <Button variant="ghost" size="sm" onClick={() => setSearchParams({ all: '1' })}>
                {t('task.graph.showAll')}
              </Button>
              {data && (
                <p className="text-xs text-muted-foreground">
                  {t('task.graph.showAllCount', { count: data.nodes.length })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {header}
        <div className="h-96 animate-pulse rounded-md bg-muted" aria-busy="true" />
      </div>
    );
  }

  if (error) {
    return <div className="space-y-6">{header}<ErrorAlert error={error} /></div>;
  }

  if (mineScoped?.notFound) {
    return (
      <div className="space-y-6">
        {header}
        <Alert variant="error" title={t('task.graph.notFoundTitle')}>
          {t('task.graph.notFoundHint')}
        </Alert>
        <Button variant="secondary" size="sm" onClick={backToPicker}>{t('task.graph.changeScope')}</Button>
      </div>
    );
  }

  const scopeControls = (
    <div className="flex flex-wrap items-center gap-3">
      {focusTaskId && (
        <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm">
          <span className="text-muted-foreground">{t('task.graph.focusLabel')}</span>
          <span className="max-w-[220px] truncate font-medium text-foreground">
            {focusNode?.title || focusTaskId.substring(0, 8)}
          </span>
          <button
            type="button"
            onClick={clearFocus}
            aria-label={t('task.graph.clearFocus')}
            className="text-muted-foreground hover:text-destructive"
          >
            ×
          </button>
        </div>
      )}

      {focusTaskId && (
        <div className="flex items-center gap-2">
          <label htmlFor="graph-depth" className="text-sm text-muted-foreground">{t('task.graph.depthLabel')}</label>
          <div className="w-24">
            <Select id="graph-depth" value={depth} onChange={(e) => setDepth(e.target.value)} aria-label={t('task.graph.depthLabel')}>
              {DEPTH_OPTIONS.map((d) => (
                <option key={d} value={d}>{d === 'all' ? t('task.graph.depthAll') : d}</option>
              ))}
            </Select>
          </div>
        </div>
      )}

      {!focusTaskId && parentId && (
        <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm">
          <span className="text-muted-foreground">{t('task.graph.parentPickerLabel')}</span>
          <span className="max-w-[220px] truncate font-medium text-foreground">{parentLabel || parentId}</span>
          <button
            type="button"
            onClick={() => { setParentId(''); setParentLabel(''); }}
            aria-label={t('task.graph.clearParent')}
            className="text-muted-foreground hover:text-destructive"
          >
            ×
          </button>
        </div>
      )}

      <button
        type="button"
        role="switch"
        aria-checked={onlyMine}
        onClick={() => setOnlyMine((v) => !v)}
        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
          onlyMine ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
        }`}
      >
        {t('task.graph.mineToggle')}
      </button>

      <button type="button" onClick={backToPicker} className="ml-auto text-sm text-primary hover:underline">
        {t('task.graph.changeScope')}
      </button>
    </div>
  );

  const honestyNotes = (
    <div className="space-y-1">
      {onlyMine && <p className="text-xs text-muted-foreground">{t('task.graph.mineHint')}</p>}
      {scoped?.note === 'clientSideFiltered' && (
        <p className="text-xs text-muted-foreground">{t('task.graph.parentClientSideHint')}</p>
      )}
      {scoped?.note === 'parentDataUnavailable' && (
        <p className="text-xs text-muted-foreground">{t('task.graph.parentDataUnavailable')}</p>
      )}
    </div>
  );

  if (!computed || computed.laid.length === 0) {
    return (
      <div className="space-y-6">
        {header}
        {scopeControls}
        {honestyNotes}
        <Alert variant="info" title={data?.nodes.length ? t('task.graph.noMatch') : t('task.graph.empty')}>
          {data?.nodes.length ? t('task.graph.noMatchHint') : t('task.graph.emptyHint')}
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}
      {scopeControls}
      {honestyNotes}

      {computed.hasCycle && (
        <Alert variant="warning" title={t('task.graph.cycleTitle')}>{t('task.graph.cycleHint')}</Alert>
      )}

      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-2 border-primary" />{t('task.graph.legendCritical')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-2 border-destructive" />{t('task.graph.legendCycle')}</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-green-500/70" />{t('task.graph.legendReady')}</span>
        {focusTaskId && (
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-2 border-primary bg-primary/20" />{t('task.graph.legendFocus')}</span>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <svg
          width={computed.width}
          height={computed.height}
          viewBox={`0 0 ${computed.width} ${computed.height}`}
          className="min-w-full"
          role="img"
          aria-label={t('task.graph.title')}
        >
          <defs>
            <marker id="task-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
            </marker>
          </defs>

          {/* Edges */}
          {computed.laidEdges.map((e, i) => {
            const from = computed.posOf.get(e.from);
            const to = computed.posOf.get(e.to);
            if (!from || !to) return null;
            const x1 = from.x + NODE_W;
            const y1 = from.y + NODE_H / 2;
            const x2 = to.x;
            const y2 = to.y + NODE_H / 2;
            const mx = (x1 + x2) / 2;
            const cls = e.back ? 'text-destructive' : e.resolved ? 'text-green-500/60' : 'text-muted-foreground/60';
            return (
              <path
                key={i}
                d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
                className={cls}
                fill="none"
                stroke="currentColor"
                strokeWidth={e.back ? 2 : 1.5}
                strokeDasharray={e.resolved ? '4 3' : undefined}
                markerEnd="url(#task-arrow)"
              />
            );
          })}

          {/* Nodes */}
          {computed.laid.map((l) => {
            const isCritical = criticalSet.has(l.id);
            const isFocus = focusTaskId === l.id;
            const status = l.node?.status ? String(l.node.status) : '';
            const ready = !!l.node?.is_ready;
            const borderCls = l.inCycle ? 'border-destructive' : isCritical ? 'border-primary' : 'border-border';
            return (
              <foreignObject key={l.id} x={l.x} y={l.y} width={NODE_W} height={NODE_H}>
                <Link
                  to={`/tasks/${l.id}`}
                  className={`relative flex h-full flex-col justify-center gap-1 rounded-md border-2 ${
                    isFocus ? 'border-primary bg-primary/10' : borderCls
                  } bg-background px-2.5 py-1.5 no-underline`}
                >
                  {isFocus && (
                    <span className="absolute -top-2 -right-2 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-semibold text-primary-foreground shadow">
                      {t('task.graph.focusBadge')}
                    </span>
                  )}
                  <span className="truncate text-xs font-medium text-foreground">{l.node?.title || l.id.substring(0, 8)}</span>
                  <span className="flex items-center gap-1">
                    {status && <Badge variant="secondary" size="sm">{formatEnumLabel(status, 'TASK_STATUS_')}</Badge>}
                    {ready && <span className="h-2 w-2 rounded-full bg-green-500" title={t('task.graph.legendReady')} />}
                    {l.node?.blocker_count > 0 && (
                      <span className="text-[10px] text-destructive">{t('task.graph.blockers', { count: l.node.blocker_count })}</span>
                    )}
                  </span>
                </Link>
              </foreignObject>
            );
          })}
        </svg>
      </div>
    </div>
  );
});

export default TaskDependencyGraphPage;
